// Profile Error Boundary Component
// Graceful error handling for profile components

import React from 'react';
import { FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import styles from './ProfileErrorBoundary.module.css';

class ProfileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to error reporting service
    console.error('Profile Error:', error, errorInfo);
    
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Report to analytics
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
        error_component: this.props.section || 'profile'
      });
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { section = 'profile section' } = this.props;
      const isDevelopment = process.env.NODE_ENV === 'development';

      // Too many errors - suggest page reload
      if (this.state.errorCount > 3) {
        return (
          <div className={styles.errorContainer}>
            <div className={styles.errorContent}>
              <FaExclamationTriangle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>
                Multiple errors detected
              </h3>
              <p className={styles.errorMessage}>
                The page is experiencing issues. Please reload to continue.
              </p>
              <button 
                onClick={this.handleReload} 
                className={styles.reloadButton}
              >
                <FaRedo /> Reload Page
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <FaExclamationTriangle className={styles.errorIcon} />
            <h3 className={styles.errorTitle}>
              Unable to load {section}
            </h3>
            <p className={styles.errorMessage}>
              We're having trouble displaying this content. Please try again.
            </p>
            
            {isDevelopment && this.state.error && (
              <details className={styles.errorDetails}>
                <summary>Error Details</summary>
                <pre className={styles.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className={styles.errorActions}>
              <button 
                onClick={this.handleReset} 
                className={styles.retryButton}
              >
                <FaRedo /> Try Again
              </button>
              
              {this.props.onGoBack && (
                <button 
                  onClick={this.props.onGoBack} 
                  className={styles.backButton}
                >
                  Go Back
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ProfileErrorBoundary;
